/**
 * useLoadingMessage Hook (#748)
 *
 * Returns a random loading message with animated spinner.
 * Includes minimum display time to prevent jarring screen flashes.
 *
 * Usage:
 * ```tsx
 * const loadingMessage = useLoadingMessage(isLoading);
 * if (loadingMessage) return <div>{loadingMessage}</div>;
 * ```
 */

import { useState, useEffect, useRef } from 'react';
import { LOADING_MESSAGES, SPINNER, MIN_DISPLAY_MS } from '../config/loadingMessages';

/**
 * Hook that returns a loading message with animated spinner.
 *
 * @param isLoading - Whether data is currently loading
 * @returns Message string with spinner, or null when not loading
 *
 * Key behaviors:
 * - Picks message ONCE when loading starts (stable across renders)
 * - Spinner rotates every 300ms
 * - Minimum 500ms display time prevents screen flashes
 */
export function useLoadingMessage(isLoading: boolean): string | null {
  // Pick message ONCE when component mounts (stable across renders)
  const [message] = useState(
    () => LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]
  );
  const [spin, setSpin] = useState(0);
  const [showMessage, setShowMessage] = useState(false);
  const startTimeRef = useRef<number>(0);

  // Handle minimum display time to prevent flashes
  useEffect(() => {
    if (isLoading) {
      startTimeRef.current = Date.now();
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: show message immediately on loading start
      setShowMessage(true);
    } else if (showMessage) {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);
      const timer = setTimeout(() => setShowMessage(false), remaining);
      return () => clearTimeout(timer);
    }
  }, [isLoading, showMessage]);

  // Spinner animation
  useEffect(() => {
    if (!showMessage) return;
    const interval = setInterval(() => {
      setSpin(s => (s + 1) % SPINNER.length);
    }, 300);
    return () => clearInterval(interval);
  }, [showMessage]);

  if (!showMessage) return null;
  return `${message} ${SPINNER[spin]}`;
}
